import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Send, Inbox, Crown, MessageSquarePlus } from "lucide-react";

export default function AdminCoaching() {
  const [threads, setThreads] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeUser, setActiveUser] = useState(null); // {user_id, user_name}
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  const loadThreads = () => api.get("/admin/coaching/threads").then(({ data }) => setThreads(data)).catch(() => {});
  const loadClients = () => api.get("/admin/users")
    .then(({ data }) => setClients(data.filter((u) => u.subscription_tier === "coaching")))
    .catch(() => {});
  useEffect(() => { loadThreads(); loadClients(); }, []);

  const openThread = async (t) => {
    setActiveUser(t);
    try {
      const { data } = await api.get("/admin/coaching/messages", { params: { user_id: t.user_id } });
      setMessages(data);
    } catch (e) { setMessages([]); }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const reply = async () => {
    const text = input.trim();
    if (!text || !activeUser) return;
    try {
      const { data } = await api.post("/admin/coaching/messages", { user_id: activeUser.user_id, text });
      setMessages((m) => [...m, data]);
      setInput("");
      loadThreads();
    } catch (e) {}
  };

  if (activeUser) {
    return (
      <div className="mt-4" data-testid="admin-coaching-thread">
        <button onClick={() => { setActiveUser(null); loadThreads(); }} className="flex items-center text-muted-foreground text-sm mb-3">
          <ChevronLeft className="h-4 w-4" /> All conversations
        </button>
        <p className="font-display text-lg font-bold mb-3">{activeUser.user_name || "Athlete"}</p>
        <div className="flex flex-col" style={{ height: "calc(100vh - 320px)" }}>
          <div className="flex-1 overflow-y-auto space-y-3 pb-4 rd-scrollbar">
            {messages.map((m) => {
              const isCoach = m.sender === "coach";
              return (
                <div key={m.id} className={`flex ${isCoach ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${isCoach ? "bg-[#ccff0c] text-black" : "bg-secondary text-foreground border border-border"}`}>
                    {isCoach && <p className="text-[10px] font-bold text-black/60 mb-0.5 flex items-center gap-1"><Crown className="h-3 w-3" /> YOU (COACH)</p>}
                    <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                    <p className={`text-[10px] mt-1 ${isCoach ? "text-black/50" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <div className="flex items-end gap-2 pt-2 border-t border-border">
            <Input data-testid="admin-coaching-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") reply(); }} placeholder="Reply to athlete…" className="bg-card border-border h-11" />
            <Button data-testid="admin-coaching-send" onClick={reply} disabled={!input.trim()} className="h-11 w-11 p-0 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] flex-shrink-0"><Send className="h-5 w-5" /></Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="admin-coaching-inbox">
      {/* Start a conversation with any 1-on-1 member */}
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1.5 flex items-center gap-1.5">
          <MessageSquarePlus className="h-3.5 w-3.5 text-[#ccff0c]" /> Message a 1-on-1 member
        </p>
        <Select value="" onValueChange={(uid) => { const c = clients.find((x) => x.id === uid); if (c) openThread({ user_id: c.id, user_name: c.name }); }}>
          <SelectTrigger data-testid="coaching-member-select" className="bg-card border-border h-11">
            <SelectValue placeholder="Select a 1-on-1 member to message…" />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border text-foreground">
            {clients.length === 0 ? (
              <SelectItem value="__none__" disabled>No 1-on-1 coaching members yet</SelectItem>
            ) : (
              clients.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`coaching-member-option-${c.id}`}>
                  {c.name || "Athlete"} · {c.email}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Conversations</p>
        {threads.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <Inbox className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">No coaching conversations yet. Pick a member above to start one.</p>
          </div>
        ) : threads.map((t) => (
          <Card key={t.user_id} data-testid={`coaching-thread-${t.user_id}`} onClick={() => openThread(t)} className="rounded-xl bg-card border-border p-3 cursor-pointer hover:border-[#ccff0c]/40 transition-colors">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm">{t.user_name || "Athlete"}</p>
                  {t.unread > 0 && (
                    <Badge data-testid={`coaching-unread-${t.user_id}`} className="bg-[#ccff0c] text-black text-[10px] h-4 px-1.5">{t.unread}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{t.last}</p>
              </div>
              <p className="text-[10px] text-muted-foreground flex-shrink-0 ml-3">{new Date(t.last_at).toLocaleDateString()}</p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
